'use client';

import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { IconX } from '@tabler/icons-react';


export interface IModal {
  children: ReactNode;
  title?: string;
  withCloseButton?: boolean;
  opened: boolean;
  size?: 'big' | '';
  onClose: () => void;
  additionalClassNames?: string;
  customPadding?: string;
  dataTestId?: string;
  closeButtonLabel?: string;
}

export const Modal = ({
  children,
  title,
  withCloseButton = true,
  size = '',
  opened,
  onClose,
  additionalClassNames = '',
  customPadding,
  dataTestId,
  closeButtonLabel = 'Close',
}: IModal) => {
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(opened);
  }, [opened]);

  const onCloseDialog = useCallback(() => {
    setIsOpen(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.code === 'Escape') onCloseDialog();
    };

    if (isOpen) window.addEventListener('keydown', handleEscapeKey);

    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onCloseDialog]);

  useEffect(() => {
    const restoreOverflow = () => {
      document.body.style.overflowY = 'auto';
      document.documentElement.style.overflowY = 'auto';
    };

    const restrictOverflow = () => {
      document.body.style.overflowY = 'hidden';
      document.documentElement.style.overflowY = 'hidden';
    };

    if (isOpen) restrictOverflow();
    else restoreOverflow();

    return () => restoreOverflow(); // Ensure overflow is restored when component unmounts
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-[999] grid place-items-center ${additionalClassNames}`} data-testid={dataTestId}>
      <div className="fixed inset-0 bg-accent-lighterGrey/95" onClick={onCloseDialog} />
      <section
        ref={ref}
        className={`relative max-h-[90vh] ${
          size === 'big' ? 'min-w-[80vw] max-w-[100vw]' : 'max-w-[50ch] md:min-w-[37.5rem]'
        }  overflow-y-auto overflow-x-hidden rounded-md border border-accent-grey bg-white p-2 sm:p-6  md:p-12`}
        role="dialog"
        aria-modal={isOpen}
        aria-label={title}
        tab-index="-1"
      >
        <div className={`flex w-full items-center ${customPadding ?? 'pb-8'}`}>
          {title && <h2 className="flex-1 text-2xl font-bold">{title}</h2>}
          {withCloseButton && (
            <button onClick={onCloseDialog} aria-label={closeButtonLabel}>
              <IconX />
            </button>
          )}
        </div>
        {children}
      </section> 
    </div>
  );
};

Modal.displayName = 'Modal';
